import { computed, DestroyRef, Directive, ElementRef, inject, input, signal } from '@angular/core'

/**
 * Measures the height its host gets from the layout and offers it as a virtual-scroller height that is
 * a whole multiple of the item size, so a list fills the space without a half-cut row at the bottom.
 * The host must take its height from its parent (e.g. `flex-grow: 1; min-height: 0`), not from content.
 *
 * Example usage:
 * <div [albertItemSnappedHeight]="58" #snap="itemSnappedHeight">
 *   <p-virtualscroller [scrollHeight]="snap.scrollHeight()" [itemSize]="58" ... />
 * </div>
 */
@Directive({
  selector: '[albertItemSnappedHeight]',
  exportAs: 'itemSnappedHeight'
})
export class ItemSnappedHeightDirective {
  public readonly itemSize = input.required<number>({ alias: 'albertItemSnappedHeight' })

  private readonly availableHeight = signal<number | null>(null)

  public readonly scrollHeight = computed(() => {
    const available = this.availableHeight()
    const itemSize = this.itemSize()
    if (available === null || itemSize <= 0) return '100%'

    return `${Math.max(1, Math.floor(available / itemSize)) * itemSize}px`
  })

  constructor() {
    const host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement
    const observer = new ResizeObserver(([entry]) => {
      if (entry) this.availableHeight.set(entry.contentRect.height)
    })
    observer.observe(host)
    inject(DestroyRef).onDestroy(() => observer.disconnect())
  }
}
